import React from 'react'
import { Bookmark, BookmarkCheck } from 'lucide-react'

import { Select, Switch } from '@/components/ui'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { mapStatusToAction, useDocumentStore } from '@/features/document'
import {
  useAddBookmark,
  useRemoveBookmark,
  useUpdateDocument,
} from '@/features/document/hooks'
import { ComparisonButton } from '@/features/version-comparison/components'
import { DOCUMENT_STATUS_DISPLAY_TEXT } from '@/features/view-tfl-uploaded/constants.ts'
import { TflStatus, UserRole } from '@/features/view-tfl-uploaded/types.ts'
import {
  getUserRole,
  isAllowedChangeTflStatus,
} from '@/features/view-tfl-uploaded/utils/validation.ts'
import { useCurrentUser } from '@/hooks/useAuth'
import { cn } from '@/utils/common'

import { useCommentStore, useTflViewerStore } from '../store'

import { ViewerTracker } from './ViewerTracker'

const VERSION_NUMBER_DECIMAL_PLACES = 1
const MIN_VERSION_COUNT_FOR_SELECTION = 1

export interface ViewerHeaderProps {
  className?: string
  documentId: string
}

export const ViewerHeader: React.FC<ViewerHeaderProps> = ({
  className,
  documentId,
}) => {
  const {
    getListVersion,
    versionIdByDocumentId,
    getIsLatestVersion,
    setVersionId,
  } = useDocumentStore()
  const listVersion = getListVersion(documentId)
  const versionId = versionIdByDocumentId[documentId]
  const isLatestVersion = getIsLatestVersion(documentId)
  const { showCommentAvatarsByDoc, setShowCommentAvatars } = useCommentStore()
  const { metadataByDocId, setDocumentMetadata } = useTflViewerStore()
  const { data: currentUser } = useCurrentUser()
  const metadata = metadataByDocId[documentId]
  const currentStatus = metadata?.status
  const { mutate: updateDocument } = useUpdateDocument()
  const { mutate: addBookmark } = useAddBookmark()
  const { mutate: removeBookmark } = useRemoveBookmark()

  const isBookmarked = metadata?.is_bookmarked

  const userRole = getUserRole(
    metadata?.owner,
    metadata?.created_by,
    metadata?.reviewers,
    currentUser?.id
  )
  const isOwnerRole = userRole === UserRole.OWNER_AUTHOR

  const isReviewerRole =
    userRole === UserRole.REVIEWER || userRole === UserRole.AUTHOR_REVIEWER

  const roleContext = {
    isReviewer: isReviewerRole,
    isOwner: isOwnerRole,
  }

  const isDisabled =
    !isOwnerRole ||
    isReviewerRole ||
    currentStatus === TflStatus.APPROVED ||
    (isOwnerRole &&
      currentStatus !== TflStatus.QUERY_RAISED &&
      currentStatus !== TflStatus.UNDER_REVISION)

  const showCommentAvatars = showCommentAvatarsByDoc[documentId] ?? true

  const handleToggleComments = (checked: boolean) => {
    setShowCommentAvatars(documentId, !checked)
  }

  const handleChangeStatus = (value: string) => {
    if (!metadata) return
    const action = mapStatusToAction(value)
    updateDocument(
      {
        documentId,
        data: {
          action: action,
          deliverable_set_id: metadata.dsId || '',
          display_name: metadata.displayName || '',
          document_type: metadata.kind as unknown as TflStatus,
        },
      },
      {
        onSuccess: () => {
          setDocumentMetadata(documentId, { status: value as TflStatus })
        },
      }
    )
  }

  const handleToggleBookmark = () => {
    if (isBookmarked) {
      removeBookmark(documentId, {
        onSuccess: () => {
          setDocumentMetadata(documentId, { is_bookmarked: false })
        },
      })
    } else {
      addBookmark(documentId, {
        onSuccess: () => {
          setDocumentMetadata(documentId, { is_bookmarked: true })
        },
      })
    }
  }

  return (
    <header className={cn('w-full bg-white', className)}>
      <div className="flex gap-2 items-center justify-between p-4 overflow-x-auto overflow-y-hidden">
        <div className="flex gap-2 items-center">
          <Select
            value={versionId || ''}
            onChange={(value) => setVersionId(documentId, value)}
            options={listVersion.map((version, index) => ({
              label:
                index === 0
                  ? `Version ${version.version_number.toFixed(VERSION_NUMBER_DECIMAL_PLACES)} - Latest`
                  : `Version ${version.version_number.toFixed(VERSION_NUMBER_DECIMAL_PLACES)}`,
              value: version.id,
            }))}
            isHiddenBorder
            valueClassName="font-semibold text-primary-900"
            placeholderClassName="font-semibold border-none text-primary-900"
            disabled={listVersion.length <= MIN_VERSION_COUNT_FOR_SELECTION}
            contentClassName="min-w-48"
            disableSorting
            noPadding
            triggerClassName="!bg-transparent"
          />
          <Separator orientation="vertical" className="h-4" />
          <ViewerTracker documentId={documentId} />
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center space-x-2 justify-items-center">
            <Switch
              id="hide-comments"
              size="sm"
              checked={!showCommentAvatars}
              onCheckedChange={handleToggleComments}
              className={cn(
                'data-[state=checked]:bg-blue-600 data-[state=unchecked]:bg-tertiary',
                'shadow-sm'
              )}
            />
            <span className="text-sm font-medium text-secondary-700 whitespace-nowrap">
              Hide Comments
            </span>
          </div>
          <Separator orientation="vertical" className="h-4" />
          {isLatestVersion && (
            <Select
              value={currentStatus}
              onChange={handleChangeStatus}
              disabled={isDisabled}
              options={Object.keys(DOCUMENT_STATUS_DISPLAY_TEXT)
                .filter(
                  (key) =>
                    key === currentStatus ||
                    isAllowedChangeTflStatus(
                      currentStatus || '',
                      key,
                      roleContext
                    )
                )
                .map((key) => ({
                  value: key,
                  label:
                    DOCUMENT_STATUS_DISPLAY_TEXT[
                      key as keyof typeof DOCUMENT_STATUS_DISPLAY_TEXT
                    ],
                }))}
            />
          )}
          <ComparisonButton documentId={documentId} />
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outlined"
                  onClick={handleToggleBookmark}
                  className="h-9 w-9 p-0"
                >
                  {isBookmarked ? (
                    <BookmarkCheck className="h-5 w-5 text-black" />
                  ) : (
                    <Bookmark className="h-5 w-5 text-foreground-fg-quaternary-400" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <span>{isBookmarked ? 'Remove bookmark' : 'Add bookmark'}</span>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
      <Separator />
    </header>
  )
}
